/**
 * chat-panel-core — the chrome-agnostic core of Keystone chat (U1 extraction).
 *
 * Holds everything that is NOT V4-page chrome:
 *   - the two chat surfaces (`KeystoneChatSurface`, `PreChatSurface`) plus
 *     their supporting `AccountNotFoundState`/`EmptyState`,
 *   - the `useKeystoneChatPanelController` hook, which assembles the
 *     lazy-create session (`useKeystoneChatSession`), the surface-filtered
 *     persona/skill/tool lists, and the non-chrome panel state (account-not-
 *     found routing, pending example seeding, the pre-chat remount epoch).
 *
 * V4's `KeystoneChatPanel.tsx` and the Flow dock's chrome both compose these
 * exports; neither forks the logic. See
 * `plans/flow-global-chat-dock/coding-standards.md` §1: chat *logic* lives
 * here, chat *chrome* lives with its host. This module has no URL-router
 * dependency of any kind — the caller owns URL state and passes
 * `conversationId` / `setConversationId` in.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { MessageSquareIcon, AlertCircleIcon } from 'lucide-react';
import { useApi } from '../../../lib/api';
import { useKeystoneChatStream } from './DurableStreamRuntimeProvider';
import { Thread } from './Thread';
import { Composer } from '../../skills/Composer';
import { LibrarySidebar } from '../../skills/LibrarySidebar';
import { LockedPersonaChip } from './LockedPersonaChip';
import { usePersonas } from '../../skills/hooks/usePersonas';
import { useSkills } from '../../skills/hooks/useSkills';
import { useTools } from '../../skills/hooks/useTools';
import {
  useKeystoneChatSession,
  type UseKeystoneChatSessionReturn,
} from './useKeystoneChatSession';
import { createKeystoneConversation } from './create-conversation';
import { useKeystoneConversationMeta } from './useKeystoneConversationMeta';
import type { PersonaListItem } from '../../../types/personas';
import type { SkillListItem } from '../../../types/skills';

/** Keystone-chat is the U2 surface tag the picker filters personas/skills by. */
export const KEYSTONE_SURFACE = 'keystone-chat' as const;

export const EXAMPLE_QUESTIONS = [
  'What happened in the last call?',
  'Who are the key stakeholders?',
  'What are the open risks?',
  'Summarize the deal status',
];

/**
 * Build a minimal `PersonaListItem`-shaped sentinel from just a name, for the
 * reload case where the conversation carries `personaName` but not the full
 * persona row. Only the `name` is meaningful; the Composer reads `name` for
 * display and uses the non-null reference solely to gate the @ persona popup
 * off (the persona is already bound server-side — it can't be re-picked).
 */
export function makeBoundPersona(name: string): PersonaListItem {
  return {
    id: `bound:${name}`,
    name,
    description: '',
    icon: null,
    content: '',
    visibility: 'personal',
    createdById: 0,
    forkedFromId: null,
    enabled: true,
    usageCount: 0,
    availableIn: [KEYSTONE_SURFACE],
    isFavorited: false,
    forkCount: 0,
    isPinned: false,
    author: { id: 0, slackUserId: '', ownerEmail: null },
  };
}

/**
 * Debounced composer-draft persistence (Wave 2 review finding #4 — "resume
 * my typing"). Both `PreChatSurface` and `KeystoneChatSurface` held
 * composer text in a bare `useState`, destroyed by ordinary in-tab
 * navigation (back-to-list, switching sessions, switching to Queue/Requests
 * which unmounts the chat tree). This hook seeds the composer once at mount
 * from `initialValue` (Flow's session-store `draftText`), and calls
 * `onDraftChange` ~400ms after the rep stops typing — never per keystroke,
 * so ordinary typing speed never floods the store with writes. `onSent`
 * clears the persisted draft SYNCHRONOUSLY (bypassing the debounce) so a
 * stale draft never reappears after the message it was drafting has
 * actually been sent.
 *
 * Both params are optional so a caller that never passes them (V4's
 * `KeystoneChatPanel.tsx`) gets byte-identical behavior to before this fix —
 * a local composer value that starts empty and is never persisted anywhere.
 *
 * Module-private: `shell-next/chat/ChatComposerV2` does NOT reuse this hook
 * directly — see that file's own `useSessionDraft` doc comment — because
 * this hook's internal state is a bare `useState`, the exact anti-pattern
 * V2 surfaces forbid under SSE re-renders (`useDraftState` is required
 * there instead). `grep -rn useComposerDraft web/app-home/src` shows the
 * only callers are in this file.
 */
function useComposerDraft(
  initialValue: string | undefined,
  onDraftChange: ((text: string) => void) | undefined,
): [string, (next: string | ((prev: string) => string)) => void, () => void] {
  const [value, setValue] = useState(initialValue ?? '');
  // Tracked out-of-band (not just read from `value`) so the functional-
  // updater overload below can resolve against the latest text
  // synchronously, without passing an updater function into `setValue`
  // itself — React 18 StrictMode double-invokes state updater functions to
  // catch impure ones, which would double-fire the debounce timer/callback
  // below if the timer-scheduling lived inside one.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush (not lose) up to 400ms of unpersisted typing on unmount — the
  // exact moment ordinary navigation away from this session happens.
  useEffect(
    () => () => {
      if (timerRef.current === null) return;
      clearTimeout(timerRef.current);
      onDraftChangeRef.current?.(valueRef.current);
    },
    [],
  );

  const setValueDebounced = useCallback((next: string | ((prev: string) => string)) => {
    const resolved = typeof next === 'function' ? next(valueRef.current) : next;
    valueRef.current = resolved;
    setValue(resolved);
    if (!onDraftChangeRef.current) return;
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onDraftChangeRef.current?.(resolved);
    }, 400);
  }, []);

  const clearSent = useCallback(() => {
    valueRef.current = '';
    setValue('');
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onDraftChangeRef.current?.('');
  }, []);

  return [value, setValueDebounced, clearSent];
}

/**
 * Slugify a skill's display name for the `/slug ` composer insert, when the
 * skill row has no explicit `slug`. Shared by both surfaces' "insert from
 * library sidebar" handlers (previously duplicated verbatim in each).
 */
function slugifySkillName(s: SkillListItem): string {
  return (
    s.slug ||
    s.name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') ||
    'skill'
  );
}

// Controller hook

export interface UseKeystoneChatPanelControllerArgs {
  accountId: string;
  /** The currently selected conversation id, or null when none is open. */
  conversationId: string | null;
  /**
   * Commit a conversation id (or clear it) to the caller's URL/state. MUST
   * accept `null` — `handleNew`/`handleExampleClick` both clear the
   * selection. `useKeystoneChatSession`'s own `setConversationId` is typed
   * `(id: string) => void` and is only ever called with a real id, so it is
   * safe to pass a narrower callback where this widened one is expected.
   */
  setConversationId: (id: string | null) => void;
  /**
   * Additive (Flow multi-session epic — coding-standards.md §1 "Additive-
   * only"): shadow the internal `useKeystoneChatSession`'s returned session
   * object with an externally-owned one (Flow's per-session store adapter,
   * `useFlowChatSessionAdapter`). The internal hook call stays UNCONDITIONAL
   * below (hooks-unconditional discipline) — its result is simply discarded
   * when this is supplied. V4's `KeystoneChatPanel.tsx` never passes this,
   * so it is byte-for-byte unaffected (zero diff).
   */
  externalSession?: UseKeystoneChatSessionReturn;
}

export interface UseKeystoneChatPanelControllerReturn {
  session: UseKeystoneChatSessionReturn;
  personas: PersonaListItem[];
  skills: SkillListItem[];
  tools: ReturnType<typeof useTools>['data'];
  /** True when the create call failed with exactly "Account not found" for
   *  the CURRENT accountId — derived, clears automatically on account switch. */
  accountNotFound: boolean;
  /** Example-question text to pre-seed the composer with (one-shot). */
  pendingExample: string | null;
  /** Pull the queued example exactly once (returns null after). */
  consumePendingExample: () => string | null;
  /** Remount key for the pre-chat surface — bumped by `handleNew` so a fresh
   *  composer instance mounts (clearing any draft left in local state). */
  prechatEpoch: number;
  /** Select an existing conversation. */
  handleSelect: (id: string) => void;
  /** Start a new chat: clears the URL selection and the session's picker
   *  slate, and remounts the pre-chat surface. NO API call (lazy create). */
  handleNew: () => Promise<void>;
  /** Example-question click: seeds the composer's first message; lazy-create
   *  happens on send like any other first message. */
  handleExampleClick: (question: string) => void;
}

export function useKeystoneChatPanelController({
  accountId,
  conversationId,
  setConversationId,
  externalSession,
}: UseKeystoneChatPanelControllerArgs): UseKeystoneChatPanelControllerReturn {
  const { apiFetch } = useApi();

  // ---- Surface-filtered library data for the Composer pickers (U2) ----------
  // usePersonas/useSkills are keyed by surface, so 'keystone-chat' lists never
  // collide in cache with the unfiltered Edit-with-AI lists. The U2 server
  // predicate (surface tag + requiresTools⊆{read,grep,glob}) already narrows
  // these — the caller just renders what it's handed.
  const personas = usePersonas(KEYSTONE_SURFACE).data ?? [];
  const skills = useSkills(KEYSTONE_SURFACE).data ?? [];
  const tools = useTools().data ?? [];

  // ---- Lazy-create create() — throws with .status on failure ---------------
  // The POST + response-validation logic lives in the assistant-ui-free leaf
  // module `create-conversation.ts` (U2 extraction) so it can be reused by a
  // shell-level provider without dragging @assistant-ui into that provider's
  // eager chunk. This wrapper just binds `apiFetch`.
  const createConversation = useCallback(
    (body: { selectedAccountId: string; personaId?: string }) =>
      createKeystoneConversation(apiFetch, body),
    [apiFetch],
  );

  // Tracks WHICH account failed import (not a boolean) so the derived
  // `accountNotFound` automatically clears when the user switches accounts —
  // a failed create on account A must not leave the full-pane "not imported"
  // state visible for account B.
  const [notFoundAccountId, setNotFoundAccountId] = useState<string | null>(null);
  const accountNotFound = notFoundAccountId === accountId;

  // Hooks-unconditional (coding-standards.md §5): this call is ALWAYS made,
  // even when `externalSession` will end up shadowing its result below — a
  // conditional hook call here would break the rules-of-hooks the moment a
  // caller ever toggled `externalSession` between renders.
  const internalSession = useKeystoneChatSession({
    accountId,
    conversationId,
    createConversation,
    // The session's own setConversationId is only ever called with a real id
    // (never null) — this widened callback is contravariance-safe to pass.
    setConversationId,
    // NOTE: the persona LOCK (hiding the picker once a conversation has ≥1
    // message) is driven by the live stream's message count — KeystoneChatSurface
    // derives `started = messageCount > 0` and gates the Composer. This hook only
    // owns the pre-chat picker + lazy create, so it never needs the message count.
  });
  const session = externalSession ?? internalSession;

  // First-send create failure → surface the existing account-not-found UI
  // (mirrors the pre-U8 handleExampleClick 404 path). We special-case
  // account-not-found because it has a dedicated full-pane state; any other
  // create error stays on `session.createError` and is surfaced inline by the
  // pre-chat surface. No orphaned state — the session already cleared its
  // pending message on failure.
  const createError = session.createError;
  const clearCreateError = session.clearCreateError;
  useEffect(() => {
    if (createError === 'Account not found') {
      setNotFoundAccountId(accountId);
      clearCreateError();
    }
  }, [createError, clearCreateError, accountId]);

  const handleSelect = useCallback(
    (id: string) => {
      setConversationId(id);
    },
    [setConversationId],
  );

  // Example-question text to pre-seed the composer. Cleared when consumed,
  // when "New Conversation" is clicked, or when a new example is clicked.
  const [pendingExample, setPendingExample] = useState<string | null>(null);
  const pendingExampleRef = useRef(pendingExample);
  pendingExampleRef.current = pendingExample;
  const consumePendingExample = useCallback(() => {
    const msg = pendingExampleRef.current;
    pendingExampleRef.current = null;
    setPendingExample(null);
    return msg;
  }, []);

  // Remount key for PreChatSurface. "New Conversation" must reset the whole
  // pre-chat surface — clearing pendingExample alone is not enough because the
  // already-seeded draft lives in PreChatSurface's own composer state, which
  // survives as long as the component instance does. Bumping the epoch
  // remounts it with a clean composer.
  const [prechatEpoch, setPrechatEpoch] = useState(0);

  // "New Conversation" is lazy now — NO API call (decision #2). Enter pending
  // state and clear the open conversation so the pre-chat surface renders.
  // Also clear any pending example so a stale click doesn't re-seed the composer.
  const handleNew = useCallback(async () => {
    session.startNew();
    setPendingExample(null);
    setPrechatEpoch((e) => e + 1);
    setConversationId(null);
  }, [session, setConversationId]);

  // Example-question click: seed the composer's first message. Lazy-create
  // happens on send like any other first message (no eager create — decision #2).
  const handleExampleClick = useCallback(
    (question: string) => {
      session.startNew();
      setConversationId(null);
      setPendingExample(question);
    },
    [session, setConversationId],
  );

  return {
    session,
    personas,
    skills,
    tools,
    accountNotFound,
    pendingExample,
    consumePendingExample,
    prechatEpoch,
    handleSelect,
    handleNew,
    handleExampleClick,
  };
}

// KeystoneChatSurface — active conversation. Lives inside the runtime provider
// so it can read the live stream (message count → persona lock; isRunning →
// composer disabled; send → live ask). Renders the @assistant-ui message list
// (Thread, untouched) with the built-in composer hidden, and the shared
// Composer below it (mirrors Edit-with-AI's ChatPanel).

export interface KeystoneChatSurfaceProps {
  conversationId: string;
  /** The session-local persona (present only when picked this session). */
  pickedPersona: PersonaListItem | null;
  personas: PersonaListItem[];
  skills: SkillListItem[];
  tools: ReturnType<typeof useTools>['data'];
  /** Additive-only composer action slot (U5, `flow-chat-multisession` epic —
   *  the one sanctioned new prop; see `Composer`'s own doc comment). Optional
   *  so V4's `KeystoneChatPanel.tsx` stays a zero-diff caller. */
  composerAccessory?: ReactNode;
  /** Debounced draft persistence (Wave 2 review finding #4) — see
   *  `useComposerDraft`'s doc comment above. Optional/additive; V4 never
   *  passes these. */
  initialDraftValue?: string;
  onDraftChange?: (text: string) => void;
}

export function KeystoneChatSurface({
  conversationId,
  pickedPersona,
  personas,
  skills,
  tools,
  composerAccessory,
  initialDraftValue,
  onDraftChange,
}: KeystoneChatSurfaceProps) {
  const { messageCount, isRunning, send } = useKeystoneChatStream();
  const { personaName: loadedPersonaName } = useKeystoneConversationMeta(conversationId);
  const [composerValue, setComposerValue, clearSentDraft] = useComposerDraft(initialDraftValue, onDraftChange);
  const [librarySidebarOpen, setLibrarySidebarOpen] = useState(false);

  // Persona lock: ≥1 message means the chat has started (decision low #7).
  const started = messageCount > 0;

  // Chip source: prefer the session-picked persona (immediately after pick /
  // first send, before the reload fetch returns); fall back to the loaded
  // conversation's personaName (reload chip — decision #3).
  const chipName = pickedPersona?.name ?? loadedPersonaName;
  const chipIcon = pickedPersona?.icon ?? null;

  // The Composer's @ persona popup is gated off when `persona !== null` (and
  // when `started`). For an existing conversation the persona is already bound
  // server-side, so we always pass a non-null persona when one is bound — even
  // after reload (where we only have the name) — to keep the picker start-only.
  const composerPersona: PersonaListItem | null =
    pickedPersona ??
    (loadedPersonaName ? makeBoundPersona(loadedPersonaName) : null);

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Existing conversation: the verbatim text (slug intact) goes straight to
      // the live stream — U5 resolves any leading /slug server-side (R4).
      void send(trimmed);
      clearSentDraft();
    },
    [send, clearSentDraft],
  );

  // Composer popup skill-pick: no-op (Composer inserts the /slug text itself).
  const handlePickSkill = useCallback((_s: SkillListItem) => {}, []);
  // Persona is bound at create for an existing conversation — picking is
  // start-only and the popup is gated off once started, so this is a no-op here.
  const handlePickPersona = useCallback((_p: PersonaListItem) => {}, []);

  // Library sidebar skill-pick: insert /slug into the composer textarea.
  const handlePickSkillFromSidebar = useCallback((s: SkillListItem) => {
    const slug = slugifySkillName(s);
    setComposerValue((prev) => {
      const trimmed = prev.trimEnd();
      if (trimmed.length === 0) return `/${slug} `;
      return `${trimmed} /${slug} `;
    });
    setLibrarySidebarOpen(false);
  }, [setComposerValue]);

  // Library sidebar persona-pick: no-op in an active conversation (persona
  // already bound server-side; sidebar renders it locked).
  const handlePickPersonaFromSidebar = useCallback((_p: PersonaListItem) => {
    setLibrarySidebarOpen(false);
  }, []);

  return (
    <div className="relative flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {chipName && <LockedPersonaChip personaName={chipName} icon={chipIcon} />}

        <div className="flex-1 min-h-0 overflow-hidden">
          <Thread hideComposer />
        </div>

        <Composer
          value={composerValue}
          onChange={setComposerValue}
          onSend={handleSend}
          personas={personas}
          skills={skills}
          tools={tools ?? []}
          persona={composerPersona}
          started={started}
          onPickPersona={handlePickPersona}
          onPickSkill={handlePickSkill}
          disabled={isRunning}
          composerAccessory={composerAccessory}
        />
      </div>

      {/* Click-outside backdrop — only when sidebar is open. */}
      {librarySidebarOpen && (
        <button
          type="button"
          aria-label="Close toolkit sidebar"
          onClick={() => setLibrarySidebarOpen(false)}
          className="absolute inset-0 z-10 cursor-default"
        />
      )}
      <LibrarySidebar
        surface={KEYSTONE_SURFACE}
        persona={composerPersona}
        onPickPersona={handlePickPersonaFromSidebar}
        onPickSkill={handlePickSkillFromSidebar}
        tools={tools ?? []}
        open={librarySidebarOpen}
        onOpenChange={setLibrarySidebarOpen}
      />
    </div>
  );
}

// PreChatSurface — no open conversation (fresh account or pending-new). Shows
// the example-question empty state and the shared Composer. The @ persona
// popup is available (chat not started, no persona locked); the first send
// lazily creates the conversation with the picked persona (decision #2).

export interface PreChatSurfaceProps {
  accountName?: string;
  session: UseKeystoneChatSessionReturn;
  personas: PersonaListItem[];
  skills: SkillListItem[];
  tools: ReturnType<typeof useTools>['data'];
  /** Example-question text to seed the composer with (one-shot). */
  initialValue: string | null;
  onConsumedInitialValue: () => void;
  onExampleClick: (question: string) => void;
  /** Debounced draft persistence (Wave 2 review finding #4) — see
   *  `useComposerDraft`'s doc comment above. Optional/additive; V4 never
   *  passes these. */
  initialDraftValue?: string;
  onDraftChange?: (text: string) => void;
}

export function PreChatSurface({
  accountName,
  session,
  personas,
  skills,
  tools,
  initialValue,
  onConsumedInitialValue,
  onExampleClick,
  initialDraftValue,
  onDraftChange,
}: PreChatSurfaceProps) {
  const [composerValue, setComposerValue, clearSentDraft] = useComposerDraft(initialDraftValue, onDraftChange);
  const [librarySidebarOpen, setLibrarySidebarOpen] = useState(false);

  // Seed the composer once when an example question is clicked, then clear the
  // parent's one-shot so it doesn't re-seed on the next render.
  const onConsumedRef = useRef(onConsumedInitialValue);
  onConsumedRef.current = onConsumedInitialValue;
  useEffect(() => {
    if (initialValue !== null) {
      setComposerValue(initialValue);
      onConsumedRef.current();
    }
  }, [initialValue, setComposerValue]);

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Lazy create on first send (decision #2): the session creates the
      // conversation with the picked personaId, commits the id (mounting the
      // runtime provider), and queues this verbatim message for the provider to
      // flush. The slug (if any) rides inline — U5 resolves it server-side.
      void session.handleSend(trimmed);
      clearSentDraft();
    },
    [session, clearSentDraft],
  );

  const handlePickPersona = useCallback(
    (p: PersonaListItem) => {
      session.pickPersona(p);
    },
    [session],
  );
  const handlePickSkill = useCallback((_s: SkillListItem) => {}, []);

  // Library sidebar persona-pick: route through session.pickPersona (same path
  // as the @ popup) and close the sidebar.
  const handlePickPersonaFromSidebar = useCallback(
    (p: PersonaListItem) => {
      session.pickPersona(p);
      setLibrarySidebarOpen(false);
    },
    [session],
  );

  // Library sidebar skill-pick: insert /slug into the composer textarea and close.
  const handlePickSkillFromSidebar = useCallback((s: SkillListItem) => {
    const slug = slugifySkillName(s);
    setComposerValue((prev) => {
      const trimmed = prev.trimEnd();
      if (trimmed.length === 0) return `/${slug} `;
      return `${trimmed} /${slug} `;
    });
    setLibrarySidebarOpen(false);
  }, [setComposerValue]);

  return (
    <div className="relative flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {session.persona && (
          <LockedPersonaChip personaName={session.persona.name} icon={session.persona.icon} />
        )}

        {/* Inline create error (non-account-not-found). The account-not-found
            case is handled by the parent's full-pane state; anything else
            surfaces here without orphaning local state (the session already
            cleared the pending message on failure). */}
        {session.createError && session.createError !== 'Account not found' && (
          <div
            role="alert"
            className="mx-4 mt-3 rounded-lg border border-pomegranate-300 dark:border-pomegranate-700 bg-bg-red-light px-3 py-2 text-sm text-content-danger dark:text-pomegranate-400"
          >
            {session.createError}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-auto">
          <EmptyState accountName={accountName} onExampleClick={onExampleClick} />
        </div>

        <Composer
          value={composerValue}
          onChange={setComposerValue}
          onSend={handleSend}
          personas={personas}
          skills={skills}
          tools={tools ?? []}
          persona={session.persona}
          started={false}
          onPickPersona={handlePickPersona}
          onPickSkill={handlePickSkill}
          disabled={session.isCreating}
        />
      </div>

      {/* Click-outside backdrop — only when sidebar is open. */}
      {librarySidebarOpen && (
        <button
          type="button"
          aria-label="Close toolkit sidebar"
          onClick={() => setLibrarySidebarOpen(false)}
          className="absolute inset-0 z-10 cursor-default"
        />
      )}
      <LibrarySidebar
        surface={KEYSTONE_SURFACE}
        persona={session.persona}
        onPickPersona={handlePickPersonaFromSidebar}
        onPickSkill={handlePickSkillFromSidebar}
        tools={tools ?? []}
        open={librarySidebarOpen}
        onOpenChange={setLibrarySidebarOpen}
      />
    </div>
  );
}

// Account not found state

export function AccountNotFoundState() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        <AlertCircleIcon className="mx-auto mb-3 size-10 text-content-tertiary dark:text-nightshade-500" />
        <p className="text-sm font-medium text-content-secondary dark:text-nightshade-400">
          This account hasn&apos;t been imported into Keystone yet, please contact Ops
        </p>
      </div>
    </div>
  );
}

// Empty state

export function EmptyState({
  accountName,
  onExampleClick,
}: {
  accountName?: string;
  onExampleClick: (question: string) => void;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-white dark:bg-nightshade-800 shadow-lg flex items-center justify-center">
          <MessageSquareIcon className="size-7 text-icon-subtle" />
        </div>
        <p className="text-base font-bold text-content-primary dark:text-nightshade-100 mb-1">
          Ask anything about {accountName || 'this account'}
        </p>
        <p className="text-xs text-content-tertiary dark:text-nightshade-500 mb-5">Powered by Keystone AI</p>
        <div className="space-y-2">
          {EXAMPLE_QUESTIONS.map((q) => (
            <button
              key={q}
              onClick={() => onExampleClick(q)}
              className="flex items-center justify-between w-full text-left rounded-xl border border-secondary dark:border-nightshade-700 bg-white/70 dark:bg-nightshade-800/70 backdrop-blur-sm px-4 py-2.5 text-sm text-content-secondary dark:text-nightshade-400 hover:bg-white/95 dark:hover:bg-nightshade-800 hover:shadow-md transition-all"
            >
              {q}
              <span className="text-content-faint dark:text-nightshade-600 ml-3 text-base">→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
